// src/CvCatalogAdmin.jsx
// Pagina admin: razionalizzazione del catalogo CV.
// Collega le righe maestranze non collegate di TUTTI gli utenti.
// Usato come tab interno di AdminToolsPage.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchJson } from './api';
import { useTranslation } from 'react-i18next';

// ─── Palette (identica a AdminToolsPage / MieiCreditiPage) ───────────────────
const GOLD        = '#d4a550';
const GOLD_DIM    = 'rgba(212,165,80,0.13)';
const GOLD_BORDER = 'rgba(212,165,80,0.28)';
const BORDER      = 'rgba(255,255,255,0.07)';
const TEXT        = '#ecedf4';
const TEXT_DIM    = '#7a7f96';
const TEXT_MED    = 'rgba(232,230,223,0.78)';
const BG2         = 'rgba(17,19,28,0.8)';
const BG3         = '#1a1a28';
const GREEN       = '#4ade80';
const GREEN_DIM   = 'rgba(74,222,128,0.13)';
const RED         = '#f87171';
const RED_DIM     = 'rgba(248,113,113,0.13)';
const BLUE        = '#9cc0ff';
const BLUE_DIM    = 'rgba(156,192,255,0.13)';

// ─── Copia da MieiCreditiPage (lista ruoli note) ─────────────────────────────
const ROLES_POSTPRODUZIONE = [
  'Assistente al Montaggio',
  'Autore al montaggio',
  'Colorist',
  'Coordinatore di Post Produzione',
  'Data Manager',
  'Editor Video / Montatore',
  'Editor Video / Montatore (Junior)',
  'Editor Video / Montatore (Senior)',
  'Finalizzatore',
  'Ingest',
  'Logger',
  'Motion Graphic Designer',
  'Postproduzione Audio',
  'Sound Designer / Montatore del Suono',
  'VFX Artist',
];

const LIMIT = 50;

// ─── Stili condivisi (stesso oggetto S di AdminToolsPage) ────────────────────
const S = {
  input: {
    background: '#0e1018',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '8px',
    color: TEXT,
    padding: '9px 12px',
    fontSize: '13px',
    outline: 'none',
    fontFamily: 'inherit',
  },
  btn: {
    background: GOLD,
    border: 'none',
    borderRadius: '8px',
    padding: '8px 14px',
    color: '#0b0d13',
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: '12px',
    fontFamily: 'Kanit, sans-serif',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
  },
  btnGhost: {
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '8px',
    padding: '7px 12px',
    color: TEXT_MED,
    cursor: 'pointer',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
  },
  btnDanger: {
    background: RED_DIM,
    border: `1px solid rgba(248,113,113,0.3)`,
    borderRadius: '8px',
    padding: '7px 12px',
    color: RED,
    cursor: 'pointer',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
  },
  btnGold: {
    background: GOLD_DIM,
    border: `1px solid ${GOLD}`,
    borderRadius: '8px',
    padding: '7px 12px',
    color: GOLD,
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
  },
  btnGreen: {
    background: GREEN_DIM,
    border: `1px solid rgba(74,222,128,0.4)`,
    borderRadius: '8px',
    padding: '7px 12px',
    color: GREEN,
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
  },
  th: {
    textAlign: 'left',
    fontSize: '11px',
    color: TEXT_DIM,
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    padding: '9px 8px',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontFamily: 'Kanit, sans-serif',
    whiteSpace: 'nowrap',
  },
  td: {
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    padding: '10px 8px',
    fontSize: '13px',
    verticalAlign: 'top',
  },
  small: { color: TEXT_DIM, fontSize: '11px' },
  badge: (color) => ({
    display: 'inline-block',
    borderRadius: '999px',
    padding: '2px 9px',
    fontSize: '10px',
    fontWeight: 700,
    background: color === 'green' ? GREEN_DIM
      : color === 'gold' ? GOLD_DIM
      : color === 'red' ? RED_DIM
      : color === 'blue' ? BLUE_DIM
      : 'rgba(120,169,240,0.15)',
    color: color === 'green' ? GREEN
      : color === 'gold' ? GOLD
      : color === 'red' ? RED
      : BLUE,
    border: `1px solid ${
      color === 'green' ? 'rgba(74,222,128,0.3)'
      : color === 'gold' ? GOLD_BORDER
      : color === 'red' ? 'rgba(248,113,113,0.3)'
      : 'rgba(156,192,255,0.3)'
    }`,
  }),
};

// ─── useDebounce ─────────────────────────────────────────────────────────────
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─── Tooltip semplice (testo troncato con title) ──────────────────────────────
function TruncatedText({ text, maxLen = 60 }) {
  if (!text) return <span style={S.small}>—</span>;
  const truncated = text.length > maxLen ? text.slice(0, maxLen) + '…' : text;
  return (
    <span
      title={text.length > maxLen ? text : undefined}
      style={{ color: TEXT, fontSize: '13px', cursor: text.length > maxLen ? 'help' : 'default' }}
    >
      {truncated}
    </span>
  );
}

// ─── Form collegamento manuale (inline sotto riga tabella) ────────────────────
function CollegaForm({ row, onDone, onCancel }) {
  const { t } = useTranslation();

  const [programQuery, setProgramQuery]       = useState('');
  const [programResults, setProgramResults]   = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [searchLoading, setSearchLoading]     = useState(false);
  const [dropdownOpen, setDropdownOpen]       = useState(false);

  const [editions, setEditions]               = useState([]);
  const [editionsLoading, setEditionsLoading] = useState(false);
  const [selectedEditions, setSelectedEditions] = useState([]);

  const [selectedRole, setSelectedRole]       = useState(
    row.ruolo && ROLES_POSTPRODUZIONE.includes(row.ruolo) ? row.ruolo : ''
  );

  const [submitting, setSubmitting]           = useState(false);
  const [submitError, setSubmitError]         = useState(null);

  const debouncedQuery = useDebounce(programQuery, 300);

  // Cerca programmi
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2 || selectedProgram) {
      setProgramResults([]);
      setDropdownOpen(false);
      return;
    }
    let cancelled = false;
    setSearchLoading(true);
    fetchJson(`/cv/programs?q=${encodeURIComponent(debouncedQuery)}`)
      .then(data => {
        if (!cancelled) {
          setProgramResults(data?.programs || []);
          setDropdownOpen(true);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setSearchLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedQuery, selectedProgram]);

  // Carica edizioni
  const selectProgram = useCallback(async (prog) => {
    setSelectedProgram(prog);
    setProgramQuery(prog.name);
    setProgramResults([]);
    setDropdownOpen(false);
    setEditions([]);
    setSelectedEditions([]);
    setEditionsLoading(true);
    try {
      const data = await fetchJson(`/cv/programs/${prog.id}/editions`);
      setEditions(data?.editions || []);
    } catch {
      setEditions([]);
    } finally {
      setEditionsLoading(false);
    }
  }, []);

  const toggleEdition = (id) => {
    setSelectedEditions(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSalva = async () => {
    if (!selectedProgram || selectedEditions.length === 0 || !selectedRole) return;
    setSubmitting(true);
    setSubmitError(null);
    const isSingle = selectedEditions.length === 1;
    let ok = 0;
    for (const edId of selectedEditions) {
      try {
        const body = {
          user_id: row.user_id,
          edition_id: edId,
          role: selectedRole,
          source_maestranza_id: row.id,
        };
        if (isSingle) {
          if (row.data_inizio_contratto) body.self_start = row.data_inizio_contratto;
          if (row.data_fine_contratto)   body.self_end   = row.data_fine_contratto;
        }
        await fetchJson('/cv/admin/credits', { method: 'POST', body: JSON.stringify(body) });
        ok++;
      } catch (e) {
        // 409 = già esistente, conta come ok
        if (e.message && (e.message.includes('già esistente') || e.message.includes('409'))) {
          ok++;
        } else {
          setSubmitError(t('admin_cv.error_collega'));
        }
      }
    }
    setSubmitting(false);
    if (ok > 0) setTimeout(() => onDone(), 600);
  };

  const canSave = selectedProgram && selectedEditions.length > 0 && selectedRole && !submitting;

  return (
    <div style={{
      background: BG3,
      border: `1px solid ${GOLD_BORDER}`,
      borderRadius: '10px',
      padding: '16px 18px',
      marginTop: '8px',
    }}>
      {/* Ricerca programma */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{
          fontSize: '10px', fontWeight: 700, color: GOLD,
          textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '5px',
        }}>
          {t('admin_cv.form_label_programma')}
        </div>
        <div style={{ position: 'relative' }}>
          {!selectedProgram ? (
            <>
              <input
                type="text"
                value={programQuery}
                onChange={e => { setProgramQuery(e.target.value); setSelectedProgram(null); }}
                onFocus={() => { if (programResults.length > 0) setDropdownOpen(true); }}
                onBlur={() => setTimeout(() => setDropdownOpen(false), 160)}
                placeholder={t('admin_cv.form_ph_programma')}
                style={{ ...S.input, width: '100%', boxSizing: 'border-box' }}
              />
              {searchLoading && (
                <span style={{ position: 'absolute', right: 10, top: '50%',
                  transform: 'translateY(-50%)', color: TEXT_DIM, fontSize: 11 }}>...</span>
              )}
              {dropdownOpen && programResults.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
                  background: '#0e1018', border: `1px solid ${BORDER}`, borderRadius: '8px',
                  marginTop: '2px', maxHeight: '200px', overflowY: 'auto',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
                }}>
                  {programResults.map(p => (
                    <div
                      key={p.id}
                      onMouseDown={() => selectProgram(p)}
                      style={{
                        padding: '8px 12px', cursor: 'pointer', fontSize: '13px', color: TEXT,
                        display: 'flex', alignItems: 'center', gap: '8px',
                        borderBottom: `1px solid ${BORDER}`,
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = BG3}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <span style={{ flex: 1 }}>{p.name}</span>
                      {p.is_verified && (
                        <span style={S.badge('gold')}>{t('crediti.badge_verificato')}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: GOLD_DIM, border: `1px solid ${GOLD_BORDER}`,
              borderRadius: '8px', padding: '7px 12px',
            }}>
              <span style={{ color: GOLD, fontSize: '13px', fontWeight: 600, flex: 1 }}>
                {selectedProgram.name}
              </span>
              <button
                type="button"
                onClick={() => { setSelectedProgram(null); setProgramQuery(''); setEditions([]); setSelectedEditions([]); }}
                style={{ background: 'none', border: 'none', color: TEXT_DIM, cursor: 'pointer', fontSize: '16px', lineHeight: 1, padding: 0 }}
              >
                ×
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Edizioni */}
      {selectedProgram && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{
            fontSize: '10px', fontWeight: 700, color: TEXT_DIM,
            textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '5px',
          }}>
            {t('admin_cv.form_label_edizioni')}
          </div>
          {editionsLoading ? (
            <span style={{ fontSize: '12px', color: TEXT_DIM }}>{t('crediti.loading_edizioni')}</span>
          ) : editions.length === 0 ? (
            <span style={{ fontSize: '12px', color: TEXT_DIM }}>{t('admin_cv.form_nessun_risultato')}</span>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              {editions.map(ed => {
                const checked = selectedEditions.includes(ed.id);
                const label = ed.edition_label
                  ? `${t('crediti.edizione_prefix')} ${ed.edition_label}${ed.year ? ` (${ed.year})` : ''}`
                  : ed.edition_no
                    ? `${t('crediti.edizione_prefix')} ${ed.edition_no}${ed.year ? ` (${ed.year})` : ''}`
                    : `ID ${ed.id}`;
                return (
                  <label key={ed.id} style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    cursor: 'pointer', padding: '6px 10px', borderRadius: '7px',
                    background: checked ? GOLD_DIM : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${checked ? GOLD_BORDER : BORDER}`,
                    fontSize: '12px', color: checked ? GOLD : TEXT_MED,
                  }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleEdition(ed.id)}
                      style={{ accentColor: GOLD, width: 13, height: 13, flexShrink: 0 }}
                    />
                    {label}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Ruolo */}
      {selectedProgram && (
        <div style={{ marginBottom: '14px' }}>
          <div style={{
            fontSize: '10px', fontWeight: 700, color: TEXT_DIM,
            textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '5px',
          }}>
            {t('admin_cv.form_label_ruolo')}
          </div>
          {row.ruolo && ROLES_POSTPRODUZIONE.includes(row.ruolo) ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: BLUE_DIM, border: `1px solid rgba(156,192,255,0.3)`,
              borderRadius: '8px', padding: '7px 12px',
            }}>
              <span style={{ color: BLUE, fontSize: '12px', fontWeight: 600, flex: 1 }}>
                {row.ruolo}
              </span>
              <span style={{ fontSize: '10px', color: BLUE, opacity: 0.7 }}>
                {t('crediti.ruolo_preselezionato')}
              </span>
            </div>
          ) : (
            <select
              value={selectedRole}
              onChange={e => setSelectedRole(e.target.value)}
              style={{
                ...S.input,
                width: '100%',
                color: selectedRole ? TEXT : TEXT_DIM,
                cursor: 'pointer',
              }}
            >
              <option value="">{t('admin_cv.form_ph_ruolo')}</option>
              {ROLES_POSTPRODUZIONE.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Errore */}
      {submitError && (
        <div style={{
          marginBottom: '10px', fontSize: '12px', color: RED,
          background: RED_DIM, borderRadius: '6px', padding: '6px 10px',
          border: `1px solid rgba(248,113,113,0.3)`,
        }}>
          {submitError}
        </div>
      )}

      {/* Bottoni */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button
          onClick={handleSalva}
          disabled={!canSave}
          style={{
            ...S.btnGreen,
            opacity: canSave ? 1 : 0.4,
            cursor: canSave ? 'pointer' : 'not-allowed',
          }}
        >
          {submitting ? t('admin_cv.saving') : t('admin_cv.btn_salva')}
        </button>
        <button onClick={onCancel} style={S.btnGhost}>
          {t('admin_cv.btn_annulla')}
        </button>
      </div>
    </div>
  );
}

// ─── Riga tabella ─────────────────────────────────────────────────────────────
function RigaAdmin({ row, onRemove, flash }) {
  const { t } = useTranslation();
  const [openForm, setOpenForm]         = useState(false);
  const [confirming, setConfirming]     = useState(false);
  const [dismissing, setDismissing]     = useState(false);

  const pm = row.proposed_match;

  // ── Conferma proposta ────────────────────────────────────────────────────
  const handleConferma = async () => {
    if (!pm || confirming) return;
    setConfirming(true);
    try {
      const body = {
        user_id: row.user_id,
        edition_id: pm.edition_id,
        role: row.ruolo && ROLES_POSTPRODUZIONE.includes(row.ruolo) ? row.ruolo : null,
        source_maestranza_id: row.id,
      };
      if (!body.role) {
        // non possiamo confermare senza ruolo — apri form manuale
        setOpenForm(true);
        setConfirming(false);
        return;
      }
      if (row.data_inizio_contratto) body.self_start = row.data_inizio_contratto;
      if (row.data_fine_contratto)   body.self_end   = row.data_fine_contratto;
      await fetchJson('/cv/admin/credits', { method: 'POST', body: JSON.stringify(body) });
      flash(t('admin_cv.done_conferma'));
      onRemove(row.id);
    } catch (e) {
      if (e.message && (e.message.includes('già esistente') || e.message.includes('409'))) {
        flash(t('admin_cv.done_conferma'));
        onRemove(row.id);
      } else {
        flash(t('admin_cv.error_conferma'), true);
      }
    } finally {
      setConfirming(false);
    }
  };

  // ── Ignora ────────────────────────────────────────────────────────────────
  const handleIgnora = async () => {
    if (dismissing) return;
    setDismissing(true);
    try {
      await fetchJson('/cv/admin/dismiss', {
        method: 'POST',
        body: JSON.stringify({ maestranza_id: row.id, user_id: row.user_id }),
      });
      flash(t('admin_cv.done_ignora'));
      onRemove(row.id);
    } catch {
      flash(t('admin_cv.error_ignora'), true);
    } finally {
      setDismissing(false);
    }
  };

  // ── Formato date ──────────────────────────────────────────────────────────
  const fmtDate = (iso) => {
    if (!iso) return t('admin_cv.no_date');
    return new Date(iso + 'T00:00:00').toLocaleDateString('it-IT', {
      day: '2-digit', month: '2-digit', year: '2-digit',
    });
  };

  // ── Score badge colore ────────────────────────────────────────────────────
  const scoreColor = pm
    ? (pm.score >= 1.0 ? 'green' : 'gold')
    : null;

  const needsRoleForConfirm = pm && !(row.ruolo && ROLES_POSTPRODUZIONE.includes(row.ruolo));

  return (
    <>
      <tr style={{ background: openForm ? 'rgba(212,165,80,0.03)' : 'transparent' }}>
        {/* Utente */}
        <td style={S.td}>
          <div style={{ color: TEXT, fontSize: '13px', fontWeight: 500 }}>
            {row.nome} {row.cognome}
          </div>
          <div style={{ ...S.small, marginTop: '2px' }}>
            uid {row.user_id}
          </div>
        </td>

        {/* Produzione */}
        <td style={{ ...S.td, maxWidth: '240px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', flexWrap: 'wrap' }}>
            <TruncatedText text={row.produzione} maxLen={55} />
            {row.multi_edition_hint && (
              <span style={S.badge('blue')}>{t('admin_cv.badge_multi_edition')}</span>
            )}
          </div>
        </td>

        {/* Ruolo */}
        <td style={S.td}>
          {row.ruolo ? (
            <span style={{ ...S.small, color: TEXT_MED }}>{row.ruolo}</span>
          ) : row.ruoli && row.ruoli.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {row.ruoli.slice(0, 2).map((r, i) => (
                <span key={i} style={{ ...S.small, color: TEXT_MED }}>{r}</span>
              ))}
            </div>
          ) : (
            <span style={S.small}>—</span>
          )}
        </td>

        {/* Date */}
        <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
          <span style={S.small}>
            {fmtDate(row.data_inizio_contratto)}
            {row.data_fine_contratto && row.data_fine_contratto !== row.data_inizio_contratto
              ? ` → ${fmtDate(row.data_fine_contratto)}`
              : ''}
          </span>
        </td>

        {/* Proposta */}
        <td style={{ ...S.td, maxWidth: '220px' }}>
          {pm ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <div style={{ color: GOLD, fontSize: '12px', fontWeight: 600 }}>
                {pm.program_name}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
                {pm.edition_label && (
                  <span style={S.badge('gold')}>
                    {t('crediti.edizione_prefix')} {pm.edition_label}
                    {pm.year ? ` (${pm.year})` : ''}
                  </span>
                )}
                <span style={S.badge(scoreColor)}>
                  {t('admin_cv.score_label', { score: pm.score.toFixed(1) })}
                </span>
              </div>
              {needsRoleForConfirm && (
                <span style={{ fontSize: '10px', color: TEXT_DIM }}>
                  (ruolo da selezionare)
                </span>
              )}
            </div>
          ) : (
            <span style={S.small}>{t('admin_cv.no_match')}</span>
          )}
        </td>

        {/* Azioni */}
        <td style={S.td}>
          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Conferma proposta — solo se ha proposed_match con edition_id */}
            {pm && pm.edition_id && (
              <button
                onClick={handleConferma}
                disabled={confirming}
                style={{ ...S.btnGold, opacity: confirming ? 0.5 : 1 }}
              >
                {confirming ? t('admin_cv.confirming') : t('admin_cv.btn_conferma')}
              </button>
            )}
            {/* Collega manuale */}
            <button
              onClick={() => setOpenForm(v => !v)}
              style={openForm
                ? { ...S.btnGhost, borderColor: GOLD_BORDER, color: GOLD }
                : S.btnGhost}
            >
              {t('admin_cv.btn_collega')}
            </button>
            {/* Ignora */}
            <button
              onClick={handleIgnora}
              disabled={dismissing}
              style={{ ...S.btnDanger, opacity: dismissing ? 0.5 : 1 }}
            >
              {dismissing ? t('admin_cv.dismissing') : t('admin_cv.btn_ignora')}
            </button>
          </div>
        </td>
      </tr>

      {/* Form collegamento manuale — riga espansa */}
      {openForm && (
        <tr>
          <td colSpan={6} style={{ padding: '0 8px 12px', borderBottom: `1px solid rgba(255,255,255,0.05)` }}>
            <CollegaForm
              row={row}
              onDone={() => {
                setOpenForm(false);
                flash(t('admin_cv.done_collega'));
                onRemove(row.id);
              }}
              onCancel={() => setOpenForm(false)}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Componente principale ────────────────────────────────────────────────────
export default function CvCatalogAdmin() {
  const { t } = useTranslation();

  const [rows, setRows]             = useState([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(false);
  const [q, setQ]                   = useState('');
  const [filterMode, setFilterMode] = useState('all'); // 'all' | 'with_match' | 'no_match'
  const [offset, setOffset]         = useState(0);

  const [msg, setMsg]               = useState('');
  const [msgErr, setMsgErr]         = useState(false);

  const debouncedQ = useDebounce(q, 400);

  const flash = useCallback((text, err = false) => {
    setMsg(text);
    setMsgErr(err);
    setTimeout(() => setMsg(''), 3000);
  }, []);

  // ── Fetch dati ───────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(LIMIT),
        offset: String(offset),
        filter: filterMode,
      });
      if (debouncedQ) params.set('q', debouncedQ);
      const data = await fetchJson(`/cv/admin/unlinked?${params.toString()}`);
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotal(data?.total ?? 0);
    } catch (e) {
      flash(t('admin_cv.error_load'), true);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, filterMode, offset, flash, t]);

  // Quando cambia query/filtro, resetta offset a 0
  useEffect(() => {
    setOffset(0);
  }, [debouncedQ, filterMode]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Rimuovi riga dalla lista locale ──────────────────────────────────────
  const handleRemove = useCallback((id) => {
    setRows(prev => prev.filter(r => r.id !== id));
    setTotal(prev => Math.max(0, prev - 1));
  }, []);

  // ── Paginazione ───────────────────────────────────────────────────────────
  const page      = Math.floor(offset / LIMIT) + 1;
  const fromRow   = total === 0 ? 0 : offset + 1;
  const toRow     = Math.min(offset + rows.length, total);
  const canPrev   = offset > 0;
  const canNext   = offset + LIMIT < total;

  // ── Filtri chip ───────────────────────────────────────────────────────────
  const filterOptions = [
    { id: 'all',        label: t('admin_cv.filter_all') },
    { id: 'with_match', label: t('admin_cv.filter_with_match') },
    { id: 'no_match',   label: t('admin_cv.filter_no_match') },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
          {/* Search */}
          <input
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={t('admin_cv.search_placeholder')}
            style={{ ...S.input, minWidth: '240px', flex: 1, maxWidth: '380px' }}
          />

          {/* Filtri */}
          <div style={{ display: 'flex', gap: '4px' }}>
            {filterOptions.map(opt => (
              <button
                key={opt.id}
                onClick={() => setFilterMode(opt.id)}
                style={{
                  padding: '7px 13px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: filterMode === opt.id ? 700 : 400,
                  cursor: 'pointer',
                  background: filterMode === opt.id ? GOLD_DIM : 'transparent',
                  border: `1px solid ${filterMode === opt.id ? GOLD_BORDER : BORDER}`,
                  color: filterMode === opt.id ? GOLD : TEXT_DIM,
                  fontFamily: 'inherit',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Contatore */}
          <span style={{
            fontSize: '12px', color: TEXT_DIM,
            background: 'rgba(255,255,255,0.04)',
            border: `1px solid ${BORDER}`,
            borderRadius: '8px',
            padding: '6px 12px',
            whiteSpace: 'nowrap',
          }}>
            {total > 0
              ? t('admin_cv.page_info', { page, from: fromRow, to: toRow, total })
              : t('admin_cv.counter', { count: 0 })}
          </span>

          {/* Aggiorna */}
          <button onClick={load} style={S.btnGhost} disabled={loading}>
            {loading ? t('admin_cv.loading') : '↻'}
          </button>
        </div>
      </div>

      {/* Flash message */}
      {msg && (
        <div style={{
          marginBottom: '12px', fontSize: '12px',
          color: msgErr ? RED : GREEN,
          background: msgErr ? RED_DIM : GREEN_DIM,
          border: `1px solid ${msgErr ? 'rgba(248,113,113,0.3)' : 'rgba(74,222,128,0.3)'}`,
          borderRadius: '8px', padding: '8px 12px',
        }}>
          {msg}
        </div>
      )}

      {/* Loading state */}
      {loading && rows.length === 0 && (
        <div style={{ textAlign: 'center', color: TEXT_DIM, padding: '40px', fontSize: '13px' }}>
          {t('admin_cv.loading')}
        </div>
      )}

      {/* Tabella */}
      {!loading && rows.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '40px', color: TEXT_DIM,
          background: BG2, borderRadius: '10px', border: `1px solid ${BORDER}`,
          fontSize: '13px',
        }}>
          {t('admin_cv.empty')}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={S.th}>{t('admin_cv.col_user')}</th>
                <th style={S.th}>{t('admin_cv.col_produzione')}</th>
                <th style={S.th}>{t('admin_cv.col_ruolo')}</th>
                <th style={S.th}>{t('admin_cv.col_date')}</th>
                <th style={S.th}>{t('admin_cv.col_proposta')}</th>
                <th style={S.th}>{t('admin_cv.col_azioni')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <RigaAdmin
                  key={row.id}
                  row={row}
                  onRemove={handleRemove}
                  flash={flash}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginazione */}
      {(canPrev || canNext) && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginTop: '16px', flexWrap: 'wrap', gap: '8px',
        }}>
          <button
            onClick={() => setOffset(o => Math.max(0, o - LIMIT))}
            disabled={!canPrev}
            style={{ ...S.btnGhost, opacity: canPrev ? 1 : 0.3, cursor: canPrev ? 'pointer' : 'default' }}
          >
            {t('admin_cv.prev')}
          </button>

          <span style={{ fontSize: '12px', color: TEXT_DIM }}>
            {t('admin_cv.page_info', { page, from: fromRow, to: toRow, total })}
          </span>

          <button
            onClick={() => setOffset(o => o + LIMIT)}
            disabled={!canNext}
            style={{ ...S.btnGhost, opacity: canNext ? 1 : 0.3, cursor: canNext ? 'pointer' : 'default' }}
          >
            {t('admin_cv.next')}
          </button>
        </div>
      )}
    </div>
  );
}
